<template>
    <div class="error-msg-wrap">
        <!-- Same layout and styles as a board card (AddEditCompanyBoardItem.vue): details on the left,
             comments on the right, stacking on narrow screens -->
        <div class="form-wrapper slide-up crm-form-wrapper" :class="formData.ID ? '' : 'crm-form-new'" :style="formData.ID ? 'width:1100px' : ''">
            <div class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <div class="qc-item-wrap crm-item-wrap crm-item-wrap-bare">
                <div>
                    <!-- Opened from a task manager: the meeting this item lives on -->
                    <div v-if="fromMeeting" class="crm-item-source">
                        <svg-icon icon="twousers" />From meeting
                        <router-link v-if="fromMeeting.CanOpen" :to="{ path: '/meetings', query: { meetingID: formData.MeetingID, itemID: formData.ID } }">{{ fromMeeting.Name }}</router-link>
                        <b v-else>{{ fromMeeting.Name }}</b>
                    </div>

                    <input v-if="formData.ID" type="text" class="h3-text-input" v-model="formData.Name" placeholder="Enter Name" @change="fieldChange()" />

                    <!-- The other meetings it's on (sql/meetingitem-mirrors.sql); the links open it there -->
                    <div v-if="formData.ID && (formData.HomeMeeting || otherMirrorMeetings.length)" class="crm-item-mirrors">
                        <svg-icon icon="copy" />
                        <span>
                            <template v-if="formData.HomeMeeting">Mirrored from <a v-if="!fromMeeting" @click.prevent="$emit('open-elsewhere', formData.HomeMeeting)">{{ formData.HomeMeeting.Name }}</a><b v-else>{{ formData.HomeMeeting.Name }}</b></template>
                            <template v-if="otherMirrorMeetings.length">{{ formData.HomeMeeting ? ', also on ' : 'Also on ' }}<template v-for="(m, i) in otherMirrorMeetings" :key="m.ID">{{ i ? ', ' : '' }}<a v-if="!fromMeeting" @click.prevent="$emit('open-elsewhere', m)">{{ m.Name }}</a><b v-else>{{ m.Name }}</b></template></template>
                        </span>
                    </div>
                    <template v-if="!formData.ID">
                        <div class="crm-item-label" style="margin-top:0">Name</div>
                        <input type="text" style="margin:0" v-model="formData.Name" />
                    </template>

                    <div class="item-row">
                        <div>
                            <div class="crm-item-label">Priority</div>
                            <select v-model="formData.Priority" @change="fieldChange()">
                                <option :value="null">No Priority</option>
                                <option value="high">High</option>
                                <option value="med">Medium</option>
                                <option value="low">Low</option>
                            </select>
                        </div>
                        <div>
                            <div class="crm-item-label">Assigned To</div>
                            <select v-model="formData.AssigneeEmployeeID" @change="fieldChange()">
                                <option :value="null">Unassigned</option>
                                <option v-for="emp in employees" :key="emp.ID" :value="emp.ID">{{ emp.Name }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="item-row">
                        <div>
                            <div class="crm-item-label">Due Date</div>
                            <input type="date" v-model="formData.DueDate" :class="dueDateNudge ? 'due-date-missing' : ''" @change="fieldChange()" />
                            <div v-if="dueDateNudge" class="due-date-nudge">No due date yet. We recommend setting one.</div>
                        </div>
                        <!-- Saved right away (meetingitems.ts /complete), same as the checkbox on the card -->
                        <div v-if="formData.ID">
                            <div class="crm-item-label">Status</div>
                            <label class="item-complete">
                                <input type="checkbox" :checked="formData.Completed" @change="toggleComplete($event)" />
                                <span>{{ formData.Completed ? 'Completed' : 'Mark completed' }}</span>
                            </label>
                            <div v-if="formData.Completed && (formData.CompletedByName || formData.CompletedDate)" class="item-hint">
                                Completed<template v-if="formData.CompletedByName"> by {{ formData.CompletedByName }}</template><template v-if="formData.CompletedDate"> · {{ completedDateLabel }}</template>
                            </div>
                        </div>
                        <!-- A new item has no Status yet: keeps Due Date half width, as in the Agenda quick add -->
                        <div v-else></div>
                    </div>

                    <div v-if="canSetPrivate && (onOthersTasks || isMirrored) && !formData.Private" class="item-hint" style="margin-top:20px">
                        {{ isMirrored ? 'This item is mirrored onto other meetings' : 'This item is on someone else\'s task manager' }}, so it can't be made private.
                    </div>
                    <template v-else-if="canSetPrivate">
                        <label class="item-private">
                            <input type="checkbox" v-model="formData.Private" @change="fieldChange()" />
                            <span class="item-private-track"><span class="item-private-knob"></span></span>
                            <span class="item-private-text">
                                <span class="item-private-title">Only visible to me</span>
                                <span class="item-hint">Other attendees won't see this card on the board or in the meeting history.</span>
                            </span>
                        </label>
                        <div v-if="formData.Private && formData.AssigneeEmployeeID && formData.AssigneeEmployeeID != $store.state.user.employeeID" class="item-private-warn">
                            {{ assigneeName || 'The assignee' }} won't see this card while it's only visible to you.
                        </div>
                    </template>

                    <!-- Same rich text description as a board card; a new item edits it straight away and saves with Submit -->
                    <div class="crm-item-label">Description</div>
                    <div v-if="editDescription || !formData.ID">
                        <div class="board-tip-tap">
                            <tip-tap v-model="formData.Description" :hide-h3="true" :hide-h4="true" :hide-table="true" />
                        </div>
                        <div v-if="formData.ID" style="margin-top:10px;display:flex">
                            <div @click="saveDescription()" class="btn" style="margin:0">Save</div>
                        </div>
                    </div>
                    <div v-else>
                        <div v-if="formData.Description" class="board-tip-tap">
                            <tip-tap :key="descriptionVersion" :model-value="formData.Description" :read-only="true" />
                        </div>
                        <div style="margin-top:10px;display:flex">
                            <div @click="() => { editDescription = true; }" class="btn" style="margin:0">{{ formData.Description ? 'Edit' : 'Add a description' }}</div>
                        </div>
                    </div>

                    <input v-if="!formData.ID" style="margin-top:20px" :class="disableButton ? 'disable-button' : ''" type="submit" :value="dueDateNudge ? 'Submit Without Due Date' : 'Submit'" @click="submit(false)" />

                    <!-- Puts this item on a personal task manager (yours, the assignee's, anyone's) or a deck -->
                    <template v-if="formData.ID && sentLoaded && (sentTo.length || quickSendTarget || canSendElsewhere)">
                        <div class="crm-item-section-head">
                            <h3>Task manager</h3>
                            <div v-if="quickSendTarget || canSendElsewhere" class="board-item-btns">
                                <div v-if="quickSendTarget == 'me'" :class="sending ? 'disable-button' : ''">
                                    <span @click="sendToTasks('me')">Send to my tasks</span>
                                </div>
                                <div v-else-if="quickSendTarget == 'assignee'" :class="sending ? 'disable-button' : ''">
                                    <span @click="sendToTasks('assignee')">Send to {{ assigneeFirstName }}'s tasks</span>
                                </div>
                                <div v-if="canSendElsewhere" :class="sending ? 'disable-button' : ''">
                                    <span @click="showSendPicker = !showSendPicker">Send to...</span>
                                </div>
                            </div>
                        </div>
                        <send-task-picker v-if="showSendPicker" :assignee-id="formData.AssigneeEmployeeID" :is-private="formData.Private" :sent-to="sentTo" :busy="sending || mirroring" :mirror-on="canMirror ? onMeetingIDs : null" :inventory="canSendInventory"
                            @send="(target) => target.Target == 'meeting' ? mirrorTo(target) : target.Target == 'inventory' ? sendToInventory(target.Location) : sendToTasks(target.Target, target)" @cancel="() => { showSendPicker = false; }" />
                        <div v-for="s in sentTo" :key="s.CardID" class="item-sent">
                            <span class="item-sent-text">Sent to
                                <router-link v-if="s.IsMine && !fromMeeting" :to="{ path: '/deck/' + s.Slug, query: { openCard: s.CardID } }">{{ s.BoardName }}</router-link>
                                <b v-else>{{ s.BoardName }}</b>
                            </span>
                            <span :class="s.IsDone ? 'item-sent-col item-sent-col-done' : 'item-sent-col'">{{ s.Column }}</span>
                            <!-- What the person it was sent to said (TaskReminders.vue) -->
                            <span v-if="s.Reminder && s.Reminder.Status == 'declined'" class="item-sent-reply">{{ firstName(s.Reminder.RecipientName) }} isn't doing this<template v-if="s.Reminder.DeclineReason">: "{{ s.Reminder.DeclineReason }}"</template></span>
                        </div>
                        <div v-if="quickSendTarget || canSendElsewhere" class="item-hint">Adds this item to the top of their task manager. It stays the same item, so changes show in both places.</div>
                    </template>

                    <!-- Not from a task manager: the item is deleted from its meeting (the API still allows it) -->
                    <div v-if="formData.ID && !fromMeeting" class="board-item-btns item-delete">
                        <div>
                            <span @click="() => { showConfirmDelete = true; }">{{ formData.HomeMeeting ? 'Remove From This Board' : 'Delete' }}</span>
                        </div>
                    </div>
                </div>
                <div v-if="formData.ID">
                    <div class="crm-comment-section">
                        <div class="crm-comments-header"><b>Comments</b></div>
                        <form class="crm-comment-form" @submit.prevent="postComment()">
                            <div><textarea v-model="newComment" placeholder="Write a comment..."></textarea></div>
                            <div style="display:flex"><input :class="!newComment.trim() || postingComment ? 'disable-button btn' : 'btn'" type="submit" value="Save" /></div>
                        </form>
                        <div v-if="commentsNewestFirst.length" class="crm-comments">
                            <div class="crm-comment" v-for="c in commentsNewestFirst" :key="c.ID">
                                <form v-if="editingCommentID == c.ID" class="crm-comment-form" style="margin-bottom:10px" @submit.prevent="saveEditComment(c)">
                                    <div><textarea v-model="editCommentText"></textarea></div>
                                    <div style="display:flex"><input :class="savingCommentEdit ? 'disable-button btn' : 'btn'" type="submit" value="Save" /></div>
                                </form>
                                <div v-else class="item-comment-text">{{ c.Comment }}</div>
                                <div class="crm-comment-meta">
                                    <span>- {{ c.AuthorName || 'Unknown' }}</span>
                                    <span class="crm-comment-date">{{ formatCommentDate(c.CreateDate) }}<template v-if="c.UpdatedDate"> (edited)</template></span>
                                    <a v-if="isMyComment(c)" @click.prevent="toggleEditComment(c)">{{ editingCommentID == c.ID ? 'Close' : 'Edit' }}</a>
                                    <span v-if="canDeleteComment(c)" class="crm-x" title="Delete comment" @click="commentPendingDelete = c"><svg-icon icon="x" style="margin-left:0" /></span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Same as a board card's files -->
                    <div class="crm-item-section-head">
                        <h3>Files</h3>
                        <div class="board-item-btns">
                            <div>
                                <span @click="() => { showAddFile = !showAddFile; showAddLink = false }">Add PDF</span>
                            </div>
                            <div>
                                <span @click="() => { showAddFile = false; showAddLink = !showAddLink }">Add Link</span>
                            </div>
                        </div>
                    </div>
                    <div v-if="showAddFile">
                        <drop-file pdf-only custom-text="Drop a PDF" @fileChange="onFileChange" style="margin-bottom: 20px" />
                    </div>
                    <form v-if="showAddLink" @submit.prevent="submitLink()" style="margin-bottom:20px">
                        <div><input class="input-slim" style="margin:0 0 10px" type="text" v-model="linkNameInput" placeholder="Name" /></div>
                        <div><input class="input-slim" style="margin:0" type="text" v-model="linkURLInput" placeholder="URL" /></div>
                        <div class="inline-plus-input" style="margin-top:10px">
                            <button style="margin:0 !important;font-size:12px" class="btn"><span>Add</span></button>
                        </div>
                    </form>
                    <div class="crm-item-file-list">
                        <div v-for="file in files" :key="file.ID">
                            <a v-if="file.IsExternal" :href="file.SpacesPath" target="_blank" rel="noopener">{{ file.Name }}</a>
                            <a v-else :href="$store.state.apiUrl + $store.state.spacesUrl + file.SpacesPath" target="_blank">{{ file.Name }}</a>
                            <span class="crm-x" title="Remove file" @click="fileToDelete = file"><svg-icon icon="x" /></span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <confirm-delete v-if="fileToDelete" @confirm="confirmFileDelete" @cancel="() => { fileToDelete = null; }" msg="Are you sure you would like to remove this file?"/>
        <confirm-delete v-if="showConfirmDelete" @confirm="deleteItemCB" @cancel="() => { showConfirmDelete = false; }" :msg="deleteMessage"/>
        <confirm-delete v-if="commentPendingDelete" @confirm="deleteComment" @cancel="() => { commentPendingDelete = null; }" msg="Are you sure you would like to delete this comment?"/>

        <!-- After sending to a task manager or checking it off: optionally move the item on the meeting board -->
        <move-column-prompt v-if="movePrompt" :message="movePrompt" :columns="columns" :current="formData.Column" :default-column="moveColumn" @move="moveToColumn($event)" @cancel="movePrompt = ''" />
    </div>
</template>


<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import ConfirmDelete from './ConfirmDelete.vue';
import DropFile from './DropFile.vue';
import SvgIcon from './SvgIcon.vue';
import TipTap from './TipTap.vue';
import MoveColumnPrompt from './MoveColumnPrompt.vue';
import SendTaskPicker from './SendTaskPicker.vue';

export default defineComponent({
    name: 'AddEditMeetingItem',
    // fromMeeting: { Name, CanOpen } when opened from a task manager rather than the meeting board.
    // columns: the meeting board's column names (from Meetings.vue), for the move prompt after Send to tasks or checking it off.
    props: [ 'item', 'employees', 'fromMeeting', 'columns' ],
    // open-elsewhere: { ID, Name, Team, ItemID } - one of the other meetings it's on, to open it there (Meetings.vue)
    emits: [ 'close', 'move-column', 'open-elsewhere' ],
    components: {
        ConfirmDelete,
        DropFile,
        SvgIcon,
        TipTap,
        MoveColumnPrompt,
        SendTaskPicker
    },
    data() {
        return {
            disableButton: false,
            formData: {} as any,
            showConfirmDelete: false,
            // Mirroring it onto another meeting (Send to... > A meeting)
            mirroring: false,
            // Submit was pressed on a new item with no due date (the next Submit goes through)
            dueDateAsked: false,

            comments: [] as any,
            commentsLoaded: false,
            newComment: '',
            postingComment: false,
            editingCommentID: null as any,
            editCommentText: '',
            savingCommentEdit: false,
            commentPendingDelete: null as any,

            sentTo: [] as any,
            sentLoaded: false,
            sending: false,
            movePrompt: '', // whose tasks it was just sent to, while the move prompt is open
            moveColumn: '',

            files: [] as any,
            showAddFile: false,
            showAddLink: false,
            linkNameInput: '',
            linkURLInput: '',
            fileToDelete: null as any,
            showSendPicker: false,

            editDescription: false,
            descriptionVersion: 0
        }
    },
    async created() {
        this.formData = JSON.parse(JSON.stringify(this.$props.item));
        this.formData.Private = !!this.formData.Private;
        // Descriptions used to be plain text (and template / AI-added items still are); show their line
        // breaks in the rich text editor, as board cards do
        if(this.formData.Description && !/<[a-z][\s\S]*>/i.test(this.formData.Description)) {
            this.formData.Description = this.formData.Description.split('\n').map((line: string) => {
                const p = document.createElement('p');
                p.textContent = line;
                return p.outerHTML;
            }).join('');
        }
        if(this.formData.ID) {
            await Promise.all([ this.loadComments(), this.loadSentTo(), this.getFiles() ]);
        }
    },
    computed: {
        // Private cards belong to an employee record: anyone can make a new card private, but only
        // the person who created a card can change it afterwards (the API enforces the same)
        canSetPrivate(): boolean {
            const employeeID = this.$store.state.user.employeeID;
            if(!employeeID) {
                return false;
            }
            return !this.formData.ID || this.formData.CreatedByEmployeeID == employeeID;
        },
        assigneeName(): string {
            const emp = (this.employees || []).find((e: any) => e.ID == this.formData.AssigneeEmployeeID);
            return emp ? emp.Name : '';
        },
        completedDateLabel(): string {
            return this.formData.CompletedDate ? new Date(this.formData.CompletedDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
        },
        assigneeFirstName(): string {
            return this.assigneeName.trim().split(/\s+/)[0] || 'the assignee';
        },
        // Newest first, under the comment box, as on a board card
        commentsNewestFirst(): any[] {
            return [ ...this.comments ].reverse();
        },
        // The one quick Send button beside Send to...: the assignee's tasks when it's assigned to someone else,
        // otherwise yours (private items only go on yours; the API enforces the same). Null once it's already there.
        quickSendTarget(): string | null {
            const me = this.$store.state.user.employeeID;
            const assignee = this.formData.AssigneeEmployeeID;
            if(assignee && assignee != me && !this.formData.Private) {
                return this.sentTo.some((s: any) => !s.IsDeck && s.OwnerEmployeeID == assignee) ? null : 'assignee';
            }
            return me && !this.sentTo.some((s: any) => s.IsMine) ? 'me' : null;
        },
        // Anyone's task manager or a deck you're on (SendTaskPicker.vue); private items stay with you
        canSendElsewhere(): boolean {
            return !!this.$store.state.user.employeeID && !this.formData.Private;
        },
        // On someone else's task manager, where a private item would vanish (the API refuses it)
        // On more than one meeting's board (the API refuses making it private)
        isMirrored(): boolean {
            return !!this.formData.HomeMeeting || !!(this.formData.MirrorMeetings || []).length;
        },
        // The meetings it's mirrored onto, other than this one
        otherMirrorMeetings(): any[] {
            return (this.formData.MirrorMeetings || []).filter((m: any) => m.ID != this.formData.MeetingID);
        },
        // Meetings it isn't on yet
        // Send to... offers A meeting on the meeting board (not from a task manager), for items that aren't private
        // The red "no due date" note, until a date is picked
        dueDateNudge(): boolean {
            return this.dueDateAsked && !this.formData.ID && !this.formData.DueDate;
        },
        canMirror(): boolean {
            return !!this.formData.ID && !this.fromMeeting && !this.formData.Private;
        },
        // SuperForm (company 1) only: Send to... > Inventory App, its project board (API sfprivate.ts)
        canSendInventory(): boolean {
            return this.$store.state.user.companyID == 1 && !!this.formData.ID && !this.formData.Private;
        },
        // Every meeting it's on already, so Send to... doesn't offer them
        onMeetingIDs(): any[] {
            return [ this.formData.HomeMeeting ? this.formData.HomeMeeting.ID : this.formData.MeetingID, ...(this.formData.MirrorMeetings || []).map((m: any) => m.ID) ];
        },
        deleteMessage(): string {
            if(this.formData.HomeMeeting) {
                return `Are you sure you would like to remove this item from this board? It stays on ${this.formData.HomeMeeting.Name}` + (this.otherMirrorMeetings.length ? ' and the other meetings it\'s mirrored onto.' : '.');
            }
            const also = [];
            if(this.sentTo.length) {
                also.push('every task manager it\'s on');
            }
            const mirrors = (this.formData.MirrorMeetings || []).length;
            if(mirrors) {
                also.push(`the ${mirrors == 1 ? 'other meeting' : mirrors + ' other meetings'} it's mirrored onto`);
            }
            return 'Are you sure you would like to delete this card?' + (also.length ? ` It will also be removed from ${also.join(' and ')}.` : '');
        },
        onOthersTasks(): boolean {
            return this.sentTo.some((s: any) => s.OwnerEmployeeID != this.$store.state.user.employeeID);
        }
    },
    methods: {
        async submit(skipClose: boolean) {
            if(!this.formData.Name) {
                alert('Include name');
                return;
            }
            // A new item with no due date: suggested once (the red note), then the next Submit goes through
            if(!this.formData.ID && !this.formData.DueDate && !this.dueDateAsked) {
                this.dueDateAsked = true;
                return;
            }

            this.disableButton = true;

            await axios.post(`${this.$store.state.apiUrl}/api/meetingitems`, this.formData)
                .then(() => {
                    if(!this.formData.ID && !skipClose) {
                        this.$emit('close');
                    }
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error')
                })

            this.disableButton = false;
        },
        // Send to... > Inventory App: a new card on its project board (nothing is kept here; the Inventory App logs it)
        async sendToInventory(location: string) {
            if(this.sending) {
                return;
            }
            this.sending = true;
            // It sends what's saved, so a just-made change goes first
            await this.submit(true);
            await axios.post(`${this.$store.state.apiUrl}/api/sfprivate/inventory/meetingitem`, { MeetingID: this.formData.MeetingID, ItemID: this.formData.ID, Location: location })
                .then(() => {
                    this.showSendPicker = false;
                    this.$store.state.quickAddToast = { text: 'Sent to the Inventory App\'s project board.' };
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error');
                })
            this.sending = false;
        },
        // Send to... > A meeting: { MeetingID, Column }
        async mirrorTo(target: any) {
            if(this.mirroring) {
                return;
            }
            this.mirroring = true;
            await axios.post(`${this.$store.state.apiUrl}/api/meetingitems/mirror`, { MeetingID: this.formData.MeetingID, ItemID: this.formData.ID, TargetMeetingID: target.MeetingID, Column: target.Column })
                .then(resp => {
                    this.formData.MirrorMeetings = [ ...(this.formData.MirrorMeetings || []), resp.data ];
                    this.showSendPicker = false;
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error.');
                })
            this.mirroring = false;
        },
        async deleteItemCB() {
            const data = {
                ID: this.formData.ID,
                MeetingID: this.formData.MeetingID
            }
            await axios.post(`${this.$store.state.apiUrl}/api/meetingitems/delete`, data)
                .then(() => {
                    this.$emit('close');
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error.')
                })

            this.showConfirmDelete = false;
        },
        saveDescription() {
            this.fieldChange();
            this.editDescription = false;
            this.descriptionVersion++;
        },
        fieldChange() {
            if(this.formData.ID) {
                this.submit(true);
            }
        },
        // Completes the item (or reopens it) right away, recording who and when
        async toggleComplete(event: any) {
            const completed = !!event.target.checked;
            await axios.post(`${this.$store.state.apiUrl}/api/meetingitems/complete`, { MeetingID: this.formData.MeetingID, ID: this.formData.ID, Completed: completed })
                .then(() => {
                    this.formData.Completed = completed;
                    this.formData.CompletedDate = completed ? new Date().toISOString() : null;
                    this.formData.CompletedByName = completed ? (this.$store.state.user.teamHubEmployee?.Name || '') : null;
                    // On the meeting board, offer to move a just-completed item to the last column (unless it's there already)
                    const columns = this.columns || [];
                    const last = columns[columns.length - 1];
                    if(completed && columns.length >= 2 && this.formData.Column != last) {
                        this.moveColumn = last;
                        this.movePrompt = 'Marked completed.';
                    }
                })
                .catch(e => {
                    console.log(e);
                    event.target.checked = !completed;
                    alert(e?.response?.data || 'There was an error');
                })
        },
        // Called by Meetings.vue when a 'comments' SSE message arrives for this card (see its
        // applyMeetingBroadcast) - just re-syncs to the canonical list, so a mid-edit draft
        // (editCommentText/newComment, kept separately) isn't clobbered by someone else's change.
        applyLiveComments(itemID: any, comments: any) {
            if(itemID != this.formData.ID) {
                return;
            }
            this.comments = comments;
            this.commentsLoaded = true;
        },
        async loadSentTo() {
            await axios.get(`${this.$store.state.apiUrl}/api/meetingitems/${this.formData.MeetingID}/sent/${this.formData.ID}`)
                .then(resp => {
                    this.sentTo = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
            this.sentLoaded = true;
        },
        // extra: the Send to picker's EmployeeID, or Slug + Column for a deck
        async sendToTasks(target: string, extra: any = {}) {
            if(this.sending) {
                return;
            }
            this.sending = true;
            // The API sends to (or reminds) the saved assignee, so make sure a just-changed one is saved first
            if(target != 'me') {
                await this.submit(true);
            }
            let sent = false;
            await axios.post(`${this.$store.state.apiUrl}/api/meetingitems/sendtotasks`, {
                ...extra,
                MeetingID: this.formData.MeetingID,
                ItemID: this.formData.ID,
                Target: target
            })
                .then(() => {
                    sent = true;
                    this.showSendPicker = false;
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error');
                })
            await this.loadSentTo();
            this.sending = false;
            // On the meeting board, offer to move it along to the next column (no prompt when it's already in the last one)
            const columns = this.columns || [];
            const next = columns[columns.indexOf(this.formData.Column) + 1];
            if(sent && next) {
                this.moveColumn = next;
                const sentTo = target == 'me' ? 'your tasks'
                    : target == 'assignee' ? `${this.assigneeFirstName}'s tasks`
                    : target == 'employee' ? (((this.employees || []).find((e: any) => e.ID == extra.EmployeeID) || {}).Name ? `${((this.employees || []).find((e: any) => e.ID == extra.EmployeeID)).Name.trim().split(/\s+/)[0]}'s tasks` : 'their tasks')
                    : 'the deck';
                this.movePrompt = `Sent to ${sentTo}.`;
            }
        },
        moveToColumn(column: string) {
            this.$emit('move-column', column);
            this.formData.Column = column;
            this.movePrompt = '';
        },
        // Files and links, as on a board card (AddEditCompanyBoardItem.vue)
        async getFiles() {
            await axios.get(`${this.$store.state.apiUrl}/api/meetingitems/${this.formData.MeetingID}/files/${this.formData.ID}`)
                .then(resp => {
                    this.files = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async onFileChange(file: any) {
            if(file) {
                const formData = new FormData();
                formData.set('MeetingID', this.formData.MeetingID);
                formData.set('ItemID', this.formData.ID);
                formData.append('file', file);
                await axios.post(`${this.$store.state.apiUrl}/api/meetingitems/file`, formData)
                    .then(() => {
                        this.showAddFile = false;
                        this.getFiles();
                    })
                    .catch(e => {
                        console.log(e);
                        alert(e?.response?.data || 'There was an error.');
                    })
            }
        },
        async submitLink() {
            if(!this.linkNameInput || !this.linkURLInput) {
                return;
            }
            let url = this.linkURLInput.trim();
            if(!/^https?:\/\//i.test(url)) {
                url = 'https://' + url;
            }
            if(!url.includes('.')) {
                alert('Make sure your URL is formatted correctly');
                return;
            }
            await axios.post(`${this.$store.state.apiUrl}/api/meetingitems/externalfile`, {
                url,
                name: this.linkNameInput,
                MeetingID: this.formData.MeetingID,
                ItemID: this.formData.ID
            })
                .then(() => {
                    this.linkURLInput = '';
                    this.linkNameInput = '';
                    this.showAddLink = false;
                    this.getFiles();
                })
                .catch(e => {
                    alert(e?.response?.data || 'There was an error.');
                })
        },
        async confirmFileDelete() {
            const file = this.fileToDelete;
            this.fileToDelete = null;
            await axios.post(`${this.$store.state.apiUrl}/api/meetingitems/file/delete`, { ID: file.ID })
                .then(() => {
                    this.getFiles();
                })
                .catch(e => {
                    alert(e?.response?.data || 'There was an error.');
                })
        },
        firstName(name: string): string {
            return String(name || 'They').trim().split(/\s+/)[0];
        },
        async loadComments() {
            this.commentsLoaded = false;
            await axios.get(`${this.$store.state.apiUrl}/api/meetingitems/${this.formData.MeetingID}/comments/${this.formData.ID}`)
                .then(resp => {
                    this.comments = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
            this.commentsLoaded = true;
        },
        isMyComment(c: any): boolean {
            return c.CreatedByEmployeeID == this.$store.state.user.employeeID;
        },
        canDeleteComment(c: any): boolean {
            return this.isMyComment(c) || this.$store.state.user.teamHubRole == 'Admin';
        },
        formatCommentDate(dateStr: string): string {
            return new Date(dateStr).toLocaleDateString(undefined, { dateStyle: 'short' });
        },
        async postComment() {
            const comment = this.newComment.trim();
            if(!comment || this.postingComment) {
                return;
            }
            this.postingComment = true;

            await axios.post(`${this.$store.state.apiUrl}/api/meetingitems/comment`, {
                MeetingID: this.formData.MeetingID,
                ItemID: this.formData.ID,
                Comment: comment
            })
                .then(resp => {
                    this.comments.push(resp.data);
                    this.newComment = '';
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error posting this comment');
                })

            this.postingComment = false;
        },
        startEditComment(c: any) {
            this.editingCommentID = c.ID;
            this.editCommentText = c.Comment;
        },
        cancelEditComment() {
            this.editingCommentID = null;
            this.editCommentText = '';
        },
        toggleEditComment(c: any) {
            if(this.editingCommentID == c.ID) {
                this.cancelEditComment();
            } else {
                this.startEditComment(c);
            }
        },
        async saveEditComment(c: any) {
            const comment = this.editCommentText.trim();
            if(!comment || this.savingCommentEdit) {
                return;
            }
            this.savingCommentEdit = true;

            await axios.post(`${this.$store.state.apiUrl}/api/meetingitems/comment/edit`, {
                ID: c.ID,
                MeetingID: this.formData.MeetingID,
                Comment: comment
            })
                .then(resp => {
                    const i = this.comments.findIndex((existing: any) => existing.ID == c.ID);
                    if(i > -1) {
                        this.comments.splice(i, 1, resp.data);
                    }
                    this.cancelEditComment();
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error saving this comment');
                })

            this.savingCommentEdit = false;
        },
        async deleteComment() {
            const c = this.commentPendingDelete;
            this.commentPendingDelete = null;
            if(!c) {
                return;
            }

            await axios.post(`${this.$store.state.apiUrl}/api/meetingitems/comment/delete`, {
                ID: c.ID,
                MeetingID: this.formData.MeetingID
            })
                .then(() => {
                    this.comments = this.comments.filter((existing: any) => existing.ID != c.ID);
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error deleting this comment');
                })
        }
    }
});
</script>

<style scoped>
/* Clear of the close button, which has no cover band to sit in here */
.item-row { display: flex; gap: 15px; }
.item-row > div { flex: 1; min-width: 0; }
.item-row select, .item-row input[type=date] { width: 100%; margin: 0; }
.item-complete { display: flex; align-items: center; gap: 8px; height: 38px; font-size: 14px; font-weight: 600; cursor: pointer; }
.item-complete input { width: 16px; height: 16px; margin: 0; }
.item-comment-text { white-space: pre-wrap; }
.item-delete { margin: 25px 0 0; }

.item-sent { display: flex; align-items: center; justify-content: space-between; gap: 10px; border: 1px solid var(--mid-gray); border-radius: 10px; padding: 9px 12px; margin-bottom: 8px; font-size: 13.5px; }
.item-sent a { color: var(--primary-color); font-weight: 700; }
.item-sent-col { font-size: 11px; font-weight: 700; border-radius: 10px; padding: 2px 9px; background: var(--light-gray); color: var(--font-dark-gray); white-space: nowrap; }
.item-sent-col-done { background: #E3F4E8; color: #2E7D46; }
.item-sent { flex-wrap: wrap; }
.item-sent-reply { flex-basis: 100%; font-size: 12.5px; color: #A33E29; }

</style>
